import { Button, Input, Trashcan } from '@fountain/fountain-ui-components';
import { IconButton, Typography } from '@material-ui/core';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import { messages } from './messages';
import { useStyles } from './styles';
import { QuestionOption } from './types';

export interface OptionsEditorProps {
  options: QuestionOption[];
  /** A standard question's options come from its definition and are read-only. */
  readOnly: boolean;
  error?: string;
  onChange: (options: QuestionOption[]) => void;
}

const PREVIEW_LIMIT = 6;

export const OptionsEditor: FC<OptionsEditorProps> = ({
  options,
  readOnly,
  error,
  onChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  if (readOnly) {
    const shown = options.slice(0, PREVIEW_LIMIT);
    const hidden = options.length - shown.length;
    return (
      <div>
        <Typography variant="body2" color="textSecondary">
          {intl.formatMessage(messages.options)}
        </Typography>
        <div className={styles.chips}>
          {shown.map(option => (
            <Typography
              key={option.value}
              variant="caption"
              className={styles.chip}
            >
              {option.label ?? option.value}
            </Typography>
          ))}
          {hidden > 0 && (
            <Typography variant="caption" className={styles.chip}>
              {intl.formatMessage(messages.moreOptions, { count: hidden })}
            </Typography>
          )}
        </div>
        <Typography variant="caption" color="textSecondary">
          {intl.formatMessage(messages.standardOptionsNote)}
        </Typography>
      </div>
    );
  }

  const update = (index: number, value: string) =>
    onChange(
      options.map((option, i) => (i === index ? { ...option, value } : option)),
    );
  const remove = (index: number) =>
    onChange(options.filter((_, i) => i !== index));
  const add = () => onChange([...options, { value: '', visible: true }]);

  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {intl.formatMessage(messages.options)}
      </Typography>
      <div className={styles.optionsList}>
        {options.map((option, index) => (
          <div className={styles.optionRow} key={index}>
            <Input
              size="small"
              rootClass={styles.optionInput}
              value={option.value}
              placeholder={intl.formatMessage(messages.optionPlaceholder, {
                number: index + 1,
              })}
              inputProps={{
                'aria-label': intl.formatMessage(messages.optionPlaceholder, {
                  number: index + 1,
                }),
              }}
              onChange={event => update(index, event.target.value)}
            />
            <IconButton
              size="small"
              aria-label={intl.formatMessage(messages.removeOption)}
              onClick={() => remove(index)}
            >
              <Trashcan viewBox="0 0 16 16" />
            </IconButton>
          </div>
        ))}
        {error && (
          <Typography variant="caption" className={styles.helperError}>
            {error}
          </Typography>
        )}
        <div>
          <Button type="secondary" size="small" autoWidth onClick={add}>
            {intl.formatMessage(messages.addOption)}
          </Button>
        </div>
      </div>
    </div>
  );
};
